import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../../../../../store/reducers/rootReducer'
import { IInitialState } from '../../../../../../../../store/initialState'
import { LOAD_USER } from '../../../../../../../../store/actions/actionsTypes'
import { Auth } from '../../../../../../../modules/API/Auth/auth'
import { I2faSetupResponse } from '../../../../../../../modules/API/Auth/models/authDataModel'
import TwoFactorButton from './TwoFactorButton'
import QrSetupModal from './QrSetupModal'
import CheckCircleIcon from '../../../../../../../generic/icons/IconLib/CheckCircleIcon'
import XCircleIcon from '../../../../../../../generic/icons/IconLib/XCircleIcon'

const TwoFactorBlock = () => {
  const dispatch = useDispatch()
  const user: IInitialState = useSelector((state: RootState) => state.mainReducer)

  const is2faEnabled = !!user?.user?.twofa

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [setupData, setSetupData] = useState<I2faSetupResponse | null>(null)

 

  const handleEnable = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    try {
      const data = await Auth.setup2fa()
      setSetupData(data)
    } catch {
      // error handling can be extended with a notification system
    } finally {
      setIsLoading(false)
    }
  }, [])

  const handleConfirmEnable = useCallback(
    async (code: string): Promise<void> => {
      setIsLoading(true)
      try {
        const res = await Auth.enable2fa(code)

        dispatch({ type: LOAD_USER, payload: res })
        setSetupData(null)
      } catch {
        // invalid code — keep modal open so user can retry
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch]
  )

  const handleDisable = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    try {
      const res = await Auth.disable2fa()
      dispatch({ type: LOAD_USER, payload: res })
    } catch {
      // error handling
    } finally {
      setIsLoading(false)
    }
  }, [dispatch])

  return (
    <>
      {setupData && (
        <QrSetupModal
          setupData={setupData}
          onConfirm={handleConfirmEnable}
          onClose={() => setSetupData(null)}
          isLoading={isLoading}
        />
      )}

      <BlockWrapper>
        <TextSection>
          <BlockTitle>אימות דו-שלבי (2FA)</BlockTitle>
          <BlockDescription>
            אימות דו-שלבי מוסיף שכבת הגנה נוספת לחשבונך — גם אם הסיסמה נפגעת, הגישה תישאר חסומה.
            לאחר ההפעלה תתבקש להזין קוד מאפליקציית אימות בכל כניסה.
          </BlockDescription>
        </TextSection>

        <StatusRow>
          <StatusBadge $active={is2faEnabled}>
            {is2faEnabled ? <CheckCircleIcon /> : <XCircleIcon />}
            <span>{is2faEnabled ? 'פעיל' : 'לא פעיל'}</span>
          </StatusBadge>

          {is2faEnabled ? (
            <TwoFactorButton
              variant="disable"
              onClick={handleDisable}
              isLoading={isLoading}
            />
          ) : (
            <TwoFactorButton
              variant="enable"
              onClick={handleEnable}
              isLoading={isLoading}
            />
          )}
        </StatusRow>
      </BlockWrapper>
    </>
  )
}

const BlockWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1em;
  padding: 1em 0.5em;
  direction: rtl;
`

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35em;
  width: 100%;
`

const BlockTitle = styled.h4`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a1a;
  direction: rtl;
`

const BlockDescription = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.55);
  line-height: 1.55;
  direction: rtl;
  text-align: right;
  max-width: 480px;
`

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1em;
  flex-direction: row-reverse;
`

const StatusBadge = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.35em;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ $active }) => ($active ? '#297f76' : 'rgba(0,0,0,0.4)')};

  svg {
    width: 1rem; height: 1rem;
  }
`

export default TwoFactorBlock
